/* Upgrade-to-Pro modal — fires when a non-Pro user tries to use an AI feature.
 *
 * Reusable across any AI feature gate. Pops a friendly card explaining:
 *  - Which feature they tried to use (icon + name)
 *  - All 3 AI features they unlock
 *  - The price + 14-day trial pitch
 *  - One-click "Start 14-day trial" → Stripe Checkout
 *
 * Used by AIScorePanel + AIValuationPanel — both catch HTTP 402 and call
 * `openUpgradeModal({ feature: 'lead-scoring' })`. The 402 handler is wired
 * via a tiny global event bus so any component can trigger the modal
 * without prop-drilling.
 */
import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { X, Sparkles, Loader2, Check, Flame, Calculator, Phone } from "lucide-react";

const FEATURES = {
  "lead-scoring": { icon: Flame, label: "AI Lead Scoring", color: "text-rose-600" },
  "valuation":    { icon: Calculator, label: "AI Property Valuation + MAO", color: "text-emerald-700" },
  "call-summary": { icon: Phone, label: "AI Call Summary + Coach Score", color: "text-indigo-600" },
};

// ─── Global event bus — open from anywhere ─────────────────────────────────
export function openUpgradeModal(detail = {}) {
  window.dispatchEvent(new CustomEvent("ispeed:upgrade-modal-open", { detail }));
}

export default function UpgradeToProModal() {
  const [open, setOpen] = useState(false);
  const [feature, setFeature] = useState("lead-scoring");
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    const handler = (e) => {
      setFeature(e.detail?.feature || "lead-scoring");
      setOpen(true);
    };
    window.addEventListener("ispeed:upgrade-modal-open", handler);
    return () => window.removeEventListener("ispeed:upgrade-modal-open", handler);
  }, []);

  if (!open) return null;

  const meta = FEATURES[feature] || FEATURES["lead-scoring"];
  const Icon = meta.icon;

  const startCheckout = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/billing/checkout", {
        tier: "pro",
        billing_cycle: "monthly",
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout — try the Pricing page.");
        setStarting(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setStarting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => setOpen(false)}
      data-testid="upgrade-to-pro-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header gradient */}
        <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-amber-500 p-6 text-white relative">
          <button
            onClick={() => setOpen(false)}
            data-testid="upgrade-modal-close"
            className="absolute top-3 right-3 p-1 hover:bg-white/20 rounded-lg"
          >
            <X size={18} />
          </button>
          <div className="inline-flex items-center gap-1.5 bg-white/20 rounded-full px-2.5 py-1 mb-3">
            <Sparkles size={11} />
            <span className="text-[10px] font-bold uppercase tracking-wider">Pro feature</span>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Icon size={22} />
            <h2 className="font-display font-extrabold text-2xl">{meta.label}</h2>
          </div>
          <p className="text-white/90 text-sm">
            Unlock the full AI suite with Pro. 14-day free trial, cancel anytime.
          </p>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="text-xs uppercase font-bold tracking-wide text-stone-500 mb-3">
            You&apos;ll unlock
          </div>
          <ul className="space-y-2.5 mb-5">
            <Bullet emoji="📞" title="Apex 2.0 — AI answers every call" sub="24/7 inbound qualification + auto-books appointments on Google Calendar" />
            <Bullet emoji="🔥" title="AI Lead Scoring" sub="Every lead scored 0-100 with reasoning + next action" />
            <Bullet emoji="🧮" title="AI Property Valuation + MAO" sub="Paste an address → ARV, repairs, max offer in 5 sec" />
            <Bullet emoji="📞" title="AI Call Coach" sub="Auto-transcribe + 0-100 grade on every recorded call" />
            <Bullet emoji="✨" title="Plus the full Pro suite" sub="AI rehab scan, deals inbox, distress filters, 100 skip-trace credits, 45% affiliate" />
          </ul>

          <div className="bg-stone-50 border rounded-xl p-4 mb-4">
            <div className="flex items-baseline gap-1">
              <span className="font-display font-extrabold text-3xl">$197</span>
              <span className="text-sm text-stone-500">/mo</span>
              <span className="ml-auto text-xs font-bold uppercase tracking-wide bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded">
                14-day trial
              </span>
            </div>
            <div className="text-[11px] text-stone-500 mt-1">
              No credit card charged for 14 days · Cancel anytime · Email support
            </div>
          </div>

          <button
            type="button"
            onClick={startCheckout}
            disabled={starting}
            data-testid="upgrade-modal-start-trial"
            className="btn-money w-full text-base flex items-center justify-center gap-2"
          >
            {starting ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={14} />
            )}
            {starting ? "Opening checkout…" : "Start 14-day free trial →"}
          </button>

          <button
            type="button"
            onClick={() => setOpen(false)}
            className="block w-full text-center text-xs text-stone-500 mt-3 hover:text-stone-700"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

function Bullet({ emoji, title, sub }) {
  return (
    <li className="flex items-start gap-2.5">
      <span className="text-lg shrink-0 leading-tight mt-0.5">{emoji}</span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-stone-900 leading-tight">{title}</div>
        <div className="text-xs text-stone-600 leading-snug mt-0.5">{sub}</div>
      </div>
    </li>
  );
}
